/**
 * Estimated gain-reduction envelope for the offline (iOS) FX path.
 *
 * There is no live DynamicsCompressorNode to read `.reduction` from when FX
 * are rendered offline, so we approximate the compressor's gain reduction
 * over time from the decoded source audio: windowed peak level -> static
 * compression curve (soft knee, matching DynamicsCompressor's default
 * 30 dB knee) -> one-pole attack/release smoothing.
 *
 * The result drives the GR meter in the FX dialog. It intentionally ignores
 * the EQ stage's level contribution — close enough for a visual meter.
 *
 * IMPORTANT: `staticReductionDb` and `computeGainReductionEnvelopeFromChannels`
 * are deliberately SELF-CONTAINED (no free variables beyond each other) so
 * fxPostProcess.js can serialize them into a Web Worker via Function.toString.
 * Keep any new constants inside the function bodies or as defaulted params.
 */

const DEFAULT_RATE = 20; // envelope samples per second (50ms windows)

/**
 * Static soft-knee compression curve: returns gain reduction in dB (<= 0)
 * for a given input level.
 * @param {number} inputDb
 * @param {number} threshold - dB
 * @param {number} ratio
 * @param {number} [kneeDb] - soft knee width (DynamicsCompressor default: 30)
 * @returns {number} reduction in dB (0 or negative)
 */
export function staticReductionDb(inputDb, threshold, ratio, kneeDb = 30) {
  if (ratio <= 1) return 0;

  const over = inputDb - threshold;
  let outDb;

  if (2 * over < -kneeDb) {
    // Below knee: no compression
    outDb = inputDb;
  } else if (2 * Math.abs(over) <= kneeDb) {
    // Inside the soft knee
    outDb =
      inputDb +
      ((1 / ratio - 1) * Math.pow(over + kneeDb / 2, 2)) / (2 * kneeDb);
  } else {
    // Above knee: full ratio
    outDb = threshold + over / ratio;
  }

  return Math.min(0, outDb - inputDb);
}

/**
 * Self-contained core: compute the envelope from raw channel data.
 * Safe to run inside a Web Worker (no module-scope references).
 *
 * @param {Float32Array[]} channels - one Float32Array per channel
 * @param {number} sampleRate
 * @param {number} numFrames
 * @param {{threshold:number, ratio:number, attack:number, release:number}} compressorSettings
 *        attack/release in milliseconds (store format)
 * @param {number} [rate] - Envelope samples per second
 * @returns {{rate:number, values:Float32Array}} values are dB, 0 or negative
 */
export function computeGainReductionEnvelopeFromChannels(
  channels,
  sampleRate,
  numFrames,
  compressorSettings,
  rate = 20
) {
  const SILENCE_DB = -100;

  const { threshold, ratio } = compressorSettings;
  const attackSec = Math.max(0.001, (compressorSettings.attack || 0) / 1000);
  const releaseSec = Math.max(0.001, (compressorSettings.release || 0) / 1000);

  const windowSize = Math.max(1, Math.round(sampleRate / rate));
  const numWindows = Math.max(1, Math.ceil(numFrames / windowSize));

  const dt = 1 / rate;
  const attackCoeff = 1 - Math.exp(-dt / attackSec);
  const releaseCoeff = 1 - Math.exp(-dt / releaseSec);

  const values = new Float32Array(numWindows);
  let current = 0; // smoothed reduction (dB, <= 0)

  for (let w = 0; w < numWindows; w++) {
    // Windowed peak across all channels
    const start = w * windowSize;
    const end = Math.min(start + windowSize, numFrames);
    let peak = 0;
    for (let ch = 0; ch < channels.length; ch++) {
      const data = channels[ch];
      for (let i = start; i < end; i++) {
        const abs = Math.abs(data[i]);
        if (abs > peak) peak = abs;
      }
    }

    const inputDb = peak > 0 ? Math.max(SILENCE_DB, 20 * Math.log10(peak)) : SILENCE_DB;
    const target = staticReductionDb(inputDb, threshold, ratio);

    // Attack when reduction is increasing (going more negative), else release
    const coeff = target < current ? attackCoeff : releaseCoeff;
    current += (target - current) * coeff;

    values[w] = current;
  }

  return { rate, values };
}

/**
 * Compute the estimated gain-reduction envelope for an AudioBuffer.
 *
 * @param {AudioBuffer} audioBuffer - Decoded source audio
 * @param {{threshold:number, ratio:number, attack:number, release:number}} compressorSettings
 *        attack/release in milliseconds (store format)
 * @param {number} [rate] - Envelope samples per second
 * @returns {{rate:number, values:Float32Array}} values are dB, 0 or negative
 */
export function computeGainReductionEnvelope(audioBuffer, compressorSettings, rate = DEFAULT_RATE) {
  const channels = [];
  for (let ch = 0; ch < audioBuffer.numberOfChannels; ch++) {
    channels.push(audioBuffer.getChannelData(ch));
  }
  return computeGainReductionEnvelopeFromChannels(
    channels,
    audioBuffer.sampleRate,
    audioBuffer.length,
    compressorSettings,
    rate
  );
}

/**
 * Read an envelope at a playback position.
 * @param {{rate:number, values:Float32Array}|null} envelope
 * @param {number} timeSec
 * @returns {number} reduction in dB (0 if no envelope)
 */
export function envelopeValueAt(envelope, timeSec) {
  if (!envelope || !envelope.values || !envelope.values.length || !(timeSec >= 0)) {
    return 0;
  }
  const idx = Math.min(
    envelope.values.length - 1,
    Math.floor(timeSec * envelope.rate)
  );
  return envelope.values[idx];
}
